import isUndefined from 'lodash.isundefined';
import React, { useState, useEffect } from 'react';
import { Configure } from 'react-instantsearch-dom';
import styled from 'styled-components';

// components
import SearchContainer from '@react/components/search/SearchContainer';
import { H4 } from '@react/styledComponents';
import CartEventAdsHits from './CartEventAdsHits';

// functionality
import { getStatesInSameRegion } from '@general/helpers/regions.helpers';
import { mediaQueries } from '@general/breakpoints';
import colors from '@general/colors';
import useViewer from '@hooks/useViewer';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useCartItemsGroupedByMember from '@hooks/useCartItemsGroupedByMember';
import productTypes from '@general/productTypes';
import { getTodayUnix } from '@general/dates';

const ContainerStyled = styled.div`
    background-color: ${colors.ultraLightGray};
    padding-bottom: 32px;
    padding-top: 32px;

    @media ${mediaQueries.md} {
        padding-bottom: 88px;
        padding-top: 72px;
    }
`;

const CartEventAds = () => {
    // ------------------------------
    // State
    // ------------------------------

    const [filters, setFilters] = useState();
    const [optionalFilters, setOptionalFilters] = useState();
    const { viewer } = useViewer();
    const { cart } = useCartFromAPI({ viewerId: viewer?.recordId });
    const { itemsGroupedByMember } = useCartItemsGroupedByMember({
        cart,
        itemCount: cart ? cart.items.length : null,
        total: cart?.total,
    });

    // ------------------------------
    // Effects
    // ------------------------------

    // Main filters
    useEffect(() => {
        if (!cart || !itemsGroupedByMember || !viewer) {
            return;
        }

        const eventCodesInCart =
            cart.items
                .filter(item => item.product_type === productTypes.EVENT)
                .map(item => item.meta_info.event_code) || [];
        const viewerEvents = viewer.events.filter(e => !!e).map(e => e.code);
        const cartAndRegisteredEvents = Array.from(new Set([...eventCodesInCart, ...viewerEvents]));
        const filteredEventCodes = filterEventCodes(itemsGroupedByMember, cartAndRegisteredEvents);
        const timestampFilter = `created_at_timestamp >= ${getTodayUnix()}`;
        const registrationOpenFilter = 'registration_open:true';
        const eventFilters =
            filteredEventCodes.length > 0
                ? filteredEventCodes.map(eventCode => `NOT event_code:${eventCode}`).join(' AND ')
                : '';

        const allFilters = [timestampFilter, eventFilters, registrationOpenFilter].filter(Boolean).join(' AND ');

        setFilters(allFilters);
    }, [cart, itemsGroupedByMember, viewer]);

    // Optional filters
    useEffect(() => {
        if (!viewer) {
            return;
        }

        const mainAddress = viewer.addresses.find(a => a.purpose === 'main');
        if (mainAddress.country === 'CA') {
            setOptionalFilters('location.country:Canada');
        }

        const states = getStatesInSameRegion(mainAddress.stateProvince);
        setOptionalFilters(states.map(state => `state_province:${state.name}`));
    }, [viewer]);

    // ------------------------------
    // Functions
    // ------------------------------
    const filterEventCodes = (memberItemGroups, eventsToCheck) => {
        const codesToFilter = [];

        eventsToCheck.forEach(code => {
            const codeExistsOnEveryMember = memberItemGroups.every(member =>
                member.itemsGroupedByType
                    .filter(type => type.type === productTypes.EVENT)
                    .some(type => type.items.some(item => item.code === code))
            );

            if (codeExistsOnEveryMember) {
                codesToFilter.push(code);
            }
        });

        return codesToFilter;
    };

    // ------------------------------
    // Render
    // ------------------------------

    if (isUndefined(filters)) {
        return null;
    }

    return (
        <ContainerStyled>
            <div className="container">
                <H4>Events You May Be Interested In</H4>

                <SearchContainer index="events_boosted_desc">
                    <Configure filters={filters} hitsPerPage={2} optionalFilters={optionalFilters} />
                    <CartEventAdsHits />
                </SearchContainer>
            </div>
        </ContainerStyled>
    );
};

export default CartEventAds;
